import { HttpTransportType, HubConnectionBuilder, HubConnectionState, type HubConnection } from '@microsoft/signalr';
import { getTabClientId } from '#lib/utils/tabClientId.ts';

let connection: HubConnection | null = null;
let desiredClientId: number | null = null;

// The hub handshake carries no ClientId header, so the tab must name its client group explicitly.
async function sendClient() {
    if (!connection || connection.state !== HubConnectionState.Connected || desiredClientId == null) return;

    try {
        await connection.invoke('SetClient', desiredClientId);
    } catch (e) {
        console.error('[eventHub] SetClient failed', e);
    }
}

export function setHubClient(clientId: number | null | undefined) {
    desiredClientId = typeof clientId === 'number' && clientId > 0 ? clientId : null;
    void sendClient();
}

export async function startEventHub(onDispatch: (message: { type: string; body: object }) => void) {
    if (connection) return connection;

    desiredClientId ??= getTabClientId();

    const url = desiredClientId == null ? '/api/events' : `/api/events?clientId=${desiredClientId}`;
    const conn = new HubConnectionBuilder()
        .withUrl(url, { skipNegotiation: true, transport: HttpTransportType.WebSockets })
        .withAutomaticReconnect()
        .withStatefulReconnect()
        .build();

    conn.on('dispatch', onDispatch);

    // A full reconnect replays the original query string, which may name a client the tab has since left.
    conn.onreconnected(() => void sendClient());

    // Fires when automatic reconnect gives up (not on transient drops). Unset the singleton so a later
    // mount can start fresh; guard against clobbering a newer connection started after an explicit stop.
    conn.onclose(() => {
        if (connection === conn) connection = null;
    });

    connection = conn;
    try {
        await conn.start();
    } catch (e) {
        // Leave the singleton unset so a later mount retries instead of memoizing a dead connection.
        if (connection === conn) connection = null;
        throw e;
    }
    await sendClient();

    return conn;
}

export async function stopEventHub() {
    desiredClientId = null;

    if (!connection) return;

    const current = connection;
    connection = null;
    await current.stop();
}
